import { useEffect, useRef } from 'react';
import { useConfirm } from '../components/confirm.js';
import { subscribeWaitingApply, setWaitingApply } from './waiting-apply.js';

/**
 * "A new version is ready — reload now?" as the redesign Confirm, not a blocking `window.confirm`
 * (S2-c). Mounted once at the app root; it subscribes to `waiting-apply` and, when a fresh update
 * arrives, asks. On OK it applies (skip-waiting + the one guarded reload); on Cancel it clears the
 * offer, and the update lands on the next cold boot — the "never mid-interaction: ask, do not act"
 * rule, now without freezing the tab behind a native modal.
 *
 * `asking` guards against the store emitting twice (a re-render or a second SW event) opening a
 * second dialog over the first.
 */
export function UpdatePrompt(): JSX.Element | null {
  const [confirmEl, ask] = useConfirm();
  const asking = useRef(false);

  useEffect(
    () =>
      subscribeWaitingApply((apply) => {
        if (apply === null || asking.current) return;
        asking.current = true;
        void ask({
          title: 'A new version is ready',
          body: 'Reload now to update Mononoke? Anything unsaved on this screen is lost.',
          confirmLabel: 'Reload',
          tone: 'plain',
        }).then((ok) => {
          asking.current = false;
          if (ok) apply();
          else setWaitingApply(null); // dismissed — it applies next time the app is opened
        });
      }),
    [ask],
  );

  return confirmEl;
}
